import cx from 'classnames';
import { Menu, MenuItems, MenuPopover } from 'components/shared/Menu';
import * as React from 'react';
import { Text } from '../typography';
import FilterMenuButton from './filter-menu-button';

type FilterDropdownProps = {
  selectedItems: string[];
  onSeeAllOptionsSelect?: () => void;
  seeAllOptionsText?: string | React.ReactNode;
  popoverLabel: string | React.ReactNode;
  menuLabel: string | React.ReactNode;
  children?: React.ReactNode;
};

const FilterDropdown = ({
  selectedItems,
  popoverLabel,
  menuLabel,
  onSeeAllOptionsSelect,
  seeAllOptionsText,
  children
}: FilterDropdownProps) => {
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const options = React.useRef<unknown[]>([]);
  const showAllOptionsItem = React.useRef(false);
  const [hasSelectedItems, setHasSelectedItems] = React.useState(false);

  React.useEffect(() => {
    // getting options values to see if any value from options is selected
    options.current =
      React.Children.map(children, element => {
        if (!React.isValidElement(element)) return undefined;
        return (element.props as { value: unknown }).value;
      }) || [];

    showAllOptionsItem.current = !!(onSeeAllOptionsSelect && seeAllOptionsText);
  }, [children, onSeeAllOptionsSelect, seeAllOptionsText]);

  React.useEffect(() => {
    const anyItemsSelected = selectedItems?.some(value => {
      return options.current.some(option => option === value);
    });

    setHasSelectedItems(anyItemsSelected);
  }, [selectedItems]);

  const menuPopoverClasses = cx(
    'z-10 w-60 rounded-md bg-white text-black shadow-lg ring-1 ring-inset ring-gray-300',
    {
      'p-6': !showAllOptionsItem.current,
      'pb-5 pl-6 pr-6 pt-6': showAllOptionsItem.current
    }
  );

  return (
    <Menu type="click">
      {({ isExpanded }) => (
        <div className="relative">
          <FilterMenuButton
            isExpanded={isExpanded}
            hasSelectedItem={hasSelectedItems}
            menuLabel={menuLabel}
            ref={buttonRef}
          />
          <MenuPopover
            className={menuPopoverClasses}
            position="absolute"
            style={{
              top: '100%',
              marginTop: '0.5rem',
              left: '-1.5rem',
              fontWeight: '400'
            }}
          >
            {popoverLabel && (
              <Text size="m" className="mb-1 inline-block text-gray-500">
                {popoverLabel}
              </Text>
            )}
            <MenuItems className="outline-none">
              {children}
              {showAllOptionsItem.current && onSeeAllOptionsSelect && (
                <div
                  onClick={onSeeAllOptionsSelect}
                  className="item mt-4 cursor-pointer pb-1 pt-1 underline hover:text-gray-700"
                  role="menuitem"
                >
                  <Text size="m">{seeAllOptionsText}</Text>
                </div>
              )}
            </MenuItems>
          </MenuPopover>
        </div>
      )}
    </Menu>
  );
};

export default FilterDropdown;
